import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { buildAdminUrl, buildPublicUrl } from "@/lib/adminOrigin";
import { SELF_SERVE_ONBOARDING_ENABLED } from "@/config/featureFlags";
import Admin from "@/pages/admin_dashboard/Admin";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const OnboardingSuccess = () => {
  const { user, signOut } = useAuth();
  const enterpriseConfirmationSentRef = useRef(false);
  const [searchParams] = useSearchParams();
  const isEnterprise = searchParams.get("type") === "enterprise";
  const profileName =
    user?.user_metadata?.first_name ||
    user?.user_metadata?.full_name?.split(" ")[0] ||
    user?.user_metadata?.name?.split(" ")[0];
  const emailName = user?.email?.split("@")[0]?.split(/[._+-]/)[0];
  const rawFirstName = profileName || emailName || "there";
  const firstName = rawFirstName.charAt(0).toUpperCase() + rawFirstName.slice(1);

  useEffect(() => {
    localStorage.removeItem("dibs_onboarding_data");
    toast.success(
      isEnterprise
        ? "Enterprise setup initiated!"
        : SELF_SERVE_ONBOARDING_ENABLED
          ? "Your 14-day Premium trial is ready!"
          : "Your Dibs plan is active",
    );
  }, [isEnterprise]);

  useEffect(() => {
    const sendEnterpriseConfirmation = async () => {
      if (!isEnterprise || !user || enterpriseConfirmationSentRef.current) return;
      enterpriseConfirmationSentRef.current = true;
      try {
        const { data, error } = await supabase
          .from("businesses")
          .select("business_email, business_name")
          .eq("user_id", user.id)
          .single();
        if (!error && data?.business_email && data?.business_name) {
          await supabase.functions.invoke("enterprise-lead-confirmation-email", {
            body: {
              businessEmail: data.business_email,
              businessName: data.business_name,
            },
          });
        }
      } catch (err) {
        console.error("Failed to send enterprise confirmation email:", err);
      }
    };
    void sendEnterpriseConfirmation();
  }, [isEnterprise, user]);

  const handleGoToDashboard = () => {
    // Force a document navigation so AuthContext reloads the newly-completed
    // business row instead of serving its pre-onboarding cached value.
    if (window.location.hostname.startsWith("admin.")) {
      window.location.replace("/");
      return;
    }
    window.location.replace(buildAdminUrl(window.location, "/"));
  };

  const handleEnterpriseExit = async () => {
    await signOut();
    window.location.replace(buildPublicUrl(window.location, "/"));
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div aria-hidden="true" className="pointer-events-none select-none">
        <Admin suppressOnboardingTour />
      </div>

      <Dialog open>
        <DialogContent
          className="admin-modal-content gap-0 overflow-hidden p-0 sm:max-w-xl [&>button]:hidden"
          overlayClassName="bg-background/60 backdrop-blur-[2px]"
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader className="items-center space-y-4 px-8 pb-5 pt-8 text-center sm:text-center">
            <img
              src="/dibs_assets/red_logo_with_transparent_bg.png"
              alt="Dibs"
              className="h-10 w-10 object-contain"
            />
            <div className="space-y-1.5">
              <DialogTitle className="text-xl font-bold text-foreground">
                {isEnterprise ? "Enterprise setup initiated" : `Welcome to Dibs, ${firstName}`}
              </DialogTitle>
              <DialogDescription className="mx-auto max-w-md leading-relaxed">
                {isEnterprise
                  ? "We’ve received your request and will contact you to finish setting up your account."
                  : !SELF_SERVE_ONBOARDING_ENABLED ? "Your dashboard is ready. Your Dibs contact can help you with the next steps." : <><strong className="font-semibold text-foreground">We’ve upgraded you to a free 14-day trial of our Premium plan.</strong>{" "}Explore all the features and decide what works best for you.</>}
              </DialogDescription>
            </div>
          </DialogHeader>

          <DialogFooter className="flex-col items-center gap-4 px-8 pb-8 pt-2 sm:flex-col sm:justify-center sm:space-x-0">
            <Button
              size="sm"
              className="standard-action-button min-w-48"
              onClick={isEnterprise ? handleEnterpriseExit : handleGoToDashboard}
            >
              {isEnterprise ? "Return to home" : "Start exploring"}
            </Button>
            <p className="max-w-sm text-center text-xs leading-relaxed text-muted-foreground">
              {isEnterprise
                ? "We’ll email you when your Enterprise workspace is approved."
                : SELF_SERVE_ONBOARDING_ENABLED
                  ? "After your free trial ends, you can continue with our Premium plan or choose another plan that works for you."
                  : "You can manage your plan any time under Settings."}
            </p>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingSuccess;
